<template>
  <lb-dialog
    :value="visible"
    heading="Edit Approver"
    content-class="bulk-approver-editor-dialog"
    :loading="saving"
    :data-testid="`${testIdPrefix}__bulk-edit-approver-dialog`"
    @input="$emit('update:visible', $event)"
  >
    <template v-slot:body>
      <div class="bulk-approver-editor-dialog__body">
        <div
          class="bulk-approver-editor-dialog__tabs"
          role="tablist"
          :data-testid="`${testIdPrefix}__bulk-edit-approver-tabs`"
        >
          <button
            type="button"
            role="tab"
            class="bulk-approver-editor-dialog__tab"
            :class="{ 'bulk-approver-editor-dialog__tab--active': activeTab === editorTabs.APPROVER }"
            :aria-selected="activeTab === editorTabs.APPROVER ? 'true' : 'false'"
            :data-testid="`${testIdPrefix}__bulk-edit-approver-tab-l1`"
            @click="activeTab = editorTabs.APPROVER"
          >
            {{ signOffLabels.APPROVER_TAB_LABEL }}
          </button>
          <button
            type="button"
            role="tab"
            class="bulk-approver-editor-dialog__tab"
            :class="{ 'bulk-approver-editor-dialog__tab--active': activeTab === editorTabs.SIGN_OFF }"
            :aria-selected="activeTab === editorTabs.SIGN_OFF ? 'true' : 'false'"
            :data-testid="`${testIdPrefix}__bulk-edit-approver-tab-sign-off`"
            @click="activeTab = editorTabs.SIGN_OFF"
          >
            {{ signOffLabels.APPROVER_LABEL }}
          </button>
        </div>

        <UserChipMultiSelect
          v-if="activeTab === editorTabs.APPROVER"
          :value="localApproverIds"
          :users="locationScopedL1Users"
          :loading="usersFetchLoading"
          variant="assignment-dialog"
          :label="signOffLabels.BULK_SEARCH_L1"
          :current-users-label="signOffLabels.BULK_CURRENT_APPROVERS"
          role-label="approver"
          placeholder="Find by name or email..."
          :data-test-id="`${testIdPrefix}__bulk-edit-approver-l1-users`"
          @input="onApproverIdsChange"
        />

        <UserChipMultiSelect
          v-else
          :value="localSignOffIds"
          :users="locationScopedL2Users"
          :loading="usersFetchLoading"
          variant="assignment-dialog"
          :label="signOffLabels.BULK_SEARCH_L2"
          :current-users-label="signOffLabels.BULK_CURRENT_APPROVERS"
          role-label="sign-off approver"
          placeholder="Find by name or email..."
          :data-test-id="`${testIdPrefix}__bulk-edit-approver-l2-users`"
          @input="onSignOffIdsChange"
        />

        <p v-if="errorMessage" class="bulk-approver-editor-dialog__error">{{ errorMessage }}</p>
      </div>
    </template>

    <template v-slot:actions>
      <div class="bulk-approver-editor-dialog__actions">
        <base-button
          label="Cancel"
          secondary
          :disabled="saving"
          :data-testid="`${testIdPrefix}__bulk-edit-approver-cancel`"
          @click="close"
        />
        <base-button
          label="Save"
          primary
          :loading="saving"
          :disabled="saving"
          :data-testid="`${testIdPrefix}__bulk-edit-approver-save`"
          @click="save"
        />
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import './BulkApproverEditorDialog.scss';
import { SIGN_OFF } from '../../utils/constants';
import UserChipMultiSelect from '../UserChipMultiSelect/UserChipMultiSelect.vue';
import { normalizeUserIdList } from '../../views/Setup/TaskList/utils/assignmentRoles';
import { areSameIdLists, buildBulkRoleTargetIds } from '../../utils/bulkApproverEditorUtils';
import {
  fetchUsersForLocations,
  filterUsersToAllowedIds,
  dropIdsNotAllowed,
} from '../../utils/locationUserAccess';

export default {
  name: 'BulkApproverEditorDialog',

  components: { UserChipMultiSelect },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: String,
      default: SIGN_OFF.EDITOR_TABS.APPROVER,
      validator: (v) => Object.values(SIGN_OFF.EDITOR_TABS).includes(v),
    },
    users: {
      type: Array,
      default: () => [],
    },
    preparerIds: {
      type: Array,
      default: () => [],
    },
    approverIdsFromTasks: {
      type: Array,
      default: () => [],
    },
    signOffIdsFromTasks: {
      type: Array,
      default: () => [],
    },
    initialApproverIds: {
      type: Array,
      default: () => [],
    },
    initialSignOffIds: {
      type: Array,
      default: () => [],
    },
    testIdPrefix: {
      type: String,
      default: 'tasklist',
    },
    locationIds: {
      type: Array,
      default: () => [],
    },
    cycleId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      activeTab: SIGN_OFF.EDITOR_TABS.APPROVER,
      localApproverIds: [],
      localSignOffIds: [],
      approverDirty: false,
      signOffDirty: false,
      baselineApproverIds: [],
      baselineSignOffIds: [],
      errorMessage: '',
      allowedUserIds: [],
      usersFetchLoading: false,
      usersFetchToken: 0,
    };
  },

  computed: {
    signOffLabels() {
      return SIGN_OFF;
    },

    editorTabs() {
      return SIGN_OFF.EDITOR_TABS;
    },

    preparerIdSet() {
      return new Set((this.preparerIds || []).map(String));
    },

    approverIdSetFromTasks() {
      return new Set((this.approverIdsFromTasks || []).map(String));
    },

    signOffIdSetFromTasks() {
      return new Set((this.signOffIdsFromTasks || []).map(String));
    },

    /** Sign-off ids that block L1 approver picks (task union + in-dialog selection). */
    blockedSignOffIdSet() {
      const set = new Set(this.signOffIdSetFromTasks);
      (this.localSignOffIds || []).forEach((id) => set.add(String(id)));
      return set;
    },

    /** Approver ids that block L2 sign-off picks (task union + in-dialog selection). */
    blockedApproverIdSet() {
      const set = new Set(this.approverIdSetFromTasks);
      (this.localApproverIds || []).forEach((id) => set.add(String(id)));
      return set;
    },

    l1AssignableUsers() {
      return (this.users || []).filter(
        (user) => user?._id && this.isAssignableApproverId(user._id),
      );
    },

    l2AssignableUsers() {
      return (this.users || []).filter(
        (user) => user?._id && this.isAssignableSignOffId(user._id),
      );
    },

    locationScopedL1Users() {
      return filterUsersToAllowedIds(this.l1AssignableUsers, this.allowedUserIds);
    },

    locationScopedL2Users() {
      return filterUsersToAllowedIds(this.l2AssignableUsers, this.allowedUserIds);
    },
  },

  watch: {
    // Immediate: the parent drawer mounts lazily, already open.
    visible: {
      handler(isOpen) {
        if (isOpen) {
          this.reset();
          this.fetchAllowedUsers();
        }
      },
      immediate: true,
    },
    initialApproverIds: {
      handler(newIds) {
        if (!this.visible) return;
        const incoming = normalizeUserIdList(newIds)
          .map(String)
          .filter((id) => this.isAssignableApproverId(id, { includeLocalSignOff: false }));
        if (areSameIdLists(incoming, this.baselineApproverIds)) return;
        this.reset();
      },
      deep: true,
    },
    initialSignOffIds: {
      handler(newIds) {
        if (!this.visible) return;
        const incoming = normalizeUserIdList(newIds)
          .map(String)
          .filter((id) => this.isAssignableSignOffId(id, { includeLocalApprover: false }));
        if (areSameIdLists(incoming, this.baselineSignOffIds)) return;
        this.reset();
      },
      deep: true,
    },
    initialTab(tab) {
      if (this.visible) this.activeTab = tab;
    },
  },

  methods: {
    isAssignableApproverId(id, { includeLocalSignOff = true } = {}) {
      const userId = String(id);
      if (this.preparerIdSet.has(userId) || this.signOffIdSetFromTasks.has(userId)) {
        return false;
      }
      if (includeLocalSignOff) {
        return !(this.localSignOffIds || []).some((entry) => String(entry) === userId);
      }
      return true;
    },

    isAssignableSignOffId(id, { includeLocalApprover = true } = {}) {
      const userId = String(id);
      if (this.preparerIdSet.has(userId) || this.approverIdSetFromTasks.has(userId)) {
        return false;
      }
      if (includeLocalApprover) {
        return !(this.localApproverIds || []).some((entry) => String(entry) === userId);
      }
      return true;
    },

    async fetchAllowedUsers() {
      const locationIds = [...new Set((this.locationIds || []).map(String).filter(Boolean))];
      if (!locationIds.length) {
        this.usersFetchToken += 1;
        this.usersFetchLoading = false;
        this.allowedUserIds = [];
        this.localApproverIds = dropIdsNotAllowed(this.localApproverIds, this.allowedUserIds);
        this.localSignOffIds = dropIdsNotAllowed(this.localSignOffIds, this.allowedUserIds);
        return;
      }
      const token = ++this.usersFetchToken;
      this.usersFetchLoading = true;
      try {
        const ids = await fetchUsersForLocations({
          locationIds,
          cycleId: this.cycleId,
        });
        if (token !== this.usersFetchToken) return;
        this.allowedUserIds = ids;
        this.localApproverIds = dropIdsNotAllowed(this.localApproverIds, ids);
        this.localSignOffIds = dropIdsNotAllowed(this.localSignOffIds, ids);
      } catch (err) {
        if (token !== this.usersFetchToken) return;
        console.error('[BulkApproverEditorDialog]: usersForLocations error', err);
      } finally {
        if (token === this.usersFetchToken) this.usersFetchLoading = false;
      }
    },

    onApproverIdsChange(ids) {
      const allowed = normalizeUserIdList(ids).filter((id) => this.isAssignableApproverId(id));
      this.localApproverIds = allowed;
      this.approverDirty = true;
      this.errorMessage = '';
    },

    onSignOffIdsChange(ids) {
      const allowed = normalizeUserIdList(ids).filter((id) => this.isAssignableSignOffId(id));
      this.localSignOffIds = allowed;
      this.signOffDirty = true;
      this.errorMessage = '';
    },

    reset() {
      this.activeTab = this.initialTab || SIGN_OFF.EDITOR_TABS.APPROVER;
      this.localApproverIds = [];
      this.localSignOffIds = [];
      this.baselineApproverIds = [...(this.initialApproverIds || [])]
        .map(String)
        .filter((id) => this.isAssignableApproverId(id, { includeLocalSignOff: false }));
      this.baselineSignOffIds = [...(this.initialSignOffIds || [])]
        .map(String)
        .filter((id) => this.isAssignableSignOffId(id, { includeLocalApprover: false }));
      this.localApproverIds = [...this.baselineApproverIds];
      this.localSignOffIds = [...this.baselineSignOffIds];
      this.approverDirty = false;
      this.signOffDirty = false;
      this.errorMessage = '';
    },

    close() {
      this.$emit('update:visible', false);
    },

    validate(approverIds, signOffIds) {
      const preparerApproverOverlap = this.$constants.TASK_LIST_CONSTANTS.ERROR_MESSAGES.PREPARER_APPROVER_OVERLAP;

      const l1Overlap = approverIds.filter((id) => this.preparerIdSet.has(String(id)));
      if (l1Overlap.length) {
        this.errorMessage = preparerApproverOverlap;
        return false;
      }

      const l1SignOffOverlap = approverIds.filter((id) => this.blockedSignOffIdSet.has(String(id)));
      if (l1SignOffOverlap.length) {
        this.errorMessage = SIGN_OFF.ERRORS.APPROVER_SOD;
        return false;
      }

      const l2PreparerOverlap = signOffIds.filter((id) => this.preparerIdSet.has(String(id)));
      if (l2PreparerOverlap.length) {
        this.errorMessage = SIGN_OFF.ERRORS.PREPARER_SOD;
        return false;
      }

      const l2ApproverOverlap = signOffIds.filter((id) => this.blockedApproverIdSet.has(String(id)));
      if (l2ApproverOverlap.length) {
        this.errorMessage = SIGN_OFF.ERRORS.APPROVER_SOD;
        return false;
      }

      if (signOffIds.length && !approverIds.length) {
        this.errorMessage = SIGN_OFF.ERRORS.ORPHAN_L2;
        return false;
      }

      this.errorMessage = '';
      return true;
    },

    buildSavePayload() {
      let approverIds = buildBulkRoleTargetIds({
        dirty: this.approverDirty,
        localIds: this.localApproverIds,
        unionIds: this.approverIdsFromTasks,
        intersectionBaselineIds: this.baselineApproverIds,
      }).filter((id) => this.isAssignableApproverId(id));

      let signOffIds = buildBulkRoleTargetIds({
        dirty: this.signOffDirty,
        localIds: this.localSignOffIds,
        unionIds: this.signOffIdsFromTasks,
        intersectionBaselineIds: this.baselineSignOffIds,
      }).filter((id) => this.isAssignableSignOffId(id));

      if (this.approverDirty) {
        const merged = new Set(approverIds.map(String));
        this.localApproverIds.forEach((id) => {
          if (this.isAssignableApproverId(id)) merged.add(String(id));
        });
        approverIds = [...merged];
      }

      if (this.signOffDirty) {
        const merged = new Set(signOffIds.map(String));
        this.localSignOffIds.forEach((id) => {
          if (this.isAssignableSignOffId(id)) merged.add(String(id));
        });
        signOffIds = [...merged];
      }

      const signOffSet = new Set(signOffIds.map(String));
      const resolvedApproverIds = approverIds.filter((id) => !signOffSet.has(String(id)));

      return { approverIds: resolvedApproverIds, signOffIds };
    },

    save() {
      if (!this.approverDirty && !this.signOffDirty) {
        this.close();
        return;
      }
      const { approverIds, signOffIds } = this.buildSavePayload();
      if (!this.validate(approverIds, signOffIds)) return;
      this.$emit('save', {
        approverIds,
        signOffIds,
      });
    },
  },
};
</script>
